import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import TablePagination from "../../../components/TablePagination";
import api from "../../../services/api";
import { authenticationService } from "../../../services/auth.service";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import toastOptions from "../../../utils/toastOptions";
import {
  faBell,
  faGear,
  faMoneyBill,
  faPencil,
  faPlus,
  faSearch,
  faTrash,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import IPersonDTO from "../../../models/IPersonDTO";
import DialogConfirm from "../../../components/DialogConfirm";
import Param from "../../../models/Param";
import IDeleteDTO from "../../../models/IDeleteDTO";
import { currencyMask, displayDateTime } from "../../../utils/masks";
import CampaignPayment from "../campaign-payment/CampaignPayment";
import IPersonCampaignDTO from "../../../models/IPersonCampaignDTO";
import IPersonBilling from "../../../models/IPersonBilling";
import moment from "../../../utils/momentConfig";

import "./campaign-person-list.css";
import { isMobile } from "react-device-detect";

interface PersonListDTO {
  id: number;
  name: string;
  congregation: any;
  dueAmount: number;
  dueDay: number;
  noOfPaymentsPaid?: number;
  welcomeDeliveredAt?: Date;
  lastBilling: IPersonBilling;
}

interface MenuItem {
  show?: boolean;
  item: React.ReactNode;
}

export default function CampaignPersonList() {
  const [isReadOnly, setReadOnly] = useState<boolean>();
  const [termSearch, setTermSearch] = useState<string>("");
  const [refresh, setRefresh] = useState<boolean>();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [dialogText, setDialogText] = useState<string[]>([]);
  var personSelected = useRef(0);
  const [delayedSearchTerm, setDelayedSearchTerm] = useState("");
  const [congregations, setCongregations] = useState<Param[]>([]);
  const [congSearch, setCongSearch] = useState<number>(-1);
  const [addPayment, setAddPayment] = useState<boolean>(false);
  const [person, setPerson] = useState<IPersonCampaignDTO>();
  const [dueAmount, setDueAmount] = useState<number>();
  const [dueDay, setDueDay] = useState<number>();
  const [canChange, setCanChange] = useState<boolean>();
  const [updatedRow, setUpdatedRow] = useState<boolean>(false);
  const [pageNo, setPageNo] = useState<number>(1);
  const [isInDebt, setIsInDebt] = useState<boolean>();
  const [isWelcomeNotDelivered, setWelcomeNotDelivered] = useState<boolean>();
  const [reminderIndex, setReminderIndex] = useState<number>(-1);
  const [typeDialog, setTypeDialog] = useState<'delete' | 'reminder'>('delete');
  const [totalRows, setTotalRows] = useState<number>(0);
  const months = moment.months();
  const [debtYear, setDebtYear] = useState<number>(-1);
  const [debtMonth, setDebtMonth] = useState<number>(-1);

  const columns = [
    {
      name: "Nome",
      sortable: true,
      grow: 2,
      cell: (row: PersonListDTO) => <div>
        <span>{row.name}</span>
        {!row.welcomeDeliveredAt &&
          <FontAwesomeIcon icon={faTriangleExclamation} 
            className="uk-text-warning uk-margin-small-left"            
            uk-tooltip="title: Mensagem de bem-vindo não enviada via WhatsApp" />
        }
        <br />
        <span className="uk-text-italic">({row.congregation.description})</span>
      </div>
    },
    {
      name: 'Valor',
      sortable: true,
      cell: (row: PersonListDTO) => <span>{currencyMask(row.dueAmount)}</span>,
      omit: canChange
    },
    {
      name: 'Pagto. Info',
      sortable: false,
      cell: (row: PersonListDTO) => <div className="uk-flex uk-flex-column">
        <span>{currencyMask(row.dueAmount)} dia {row.dueDay}</span>
        <div>
          <span className="uk-text-bold">{row.noOfPaymentsPaid ?? 0}</span>
          <span className="uk-text-muted"> paga(s) de 60</span>
        </div>
        {row.lastBilling && row.lastBilling.id > 0 && (
          <>
            <span className="uk-text-muted uk-text-italic">Últ. Pagto Ref: {row.lastBilling.month}/{row.lastBilling.year}</span>
            <span className="uk-text-muted uk-text-xsmall">Registrado em {displayDateTime(row.lastBilling.paidAt)}</span>
          </>
        )}
      </div>,
      omit: !canChange
    },
    {
      name: "Ações",
      button: true,
      sortable: false,
      cell: (row: IPersonCampaignDTO) => (
        isMobile ? 
          <div className="uk-inline">
            {reminderIndex === row.id ? <div uk-spinner="true; ratio:1" className="uk-text-primary"></div> : <>
              <a href="#" onClick={(ev) => {ev.preventDefault();}}>
                <FontAwesomeIcon icon={faGear} size="lg" />
              </a>
              <div uk-dropdown="mode: click; pos: center; boundary: ! .uk-button-group;">
                <ul className="uk-nav uk-dropdown-nav">
                  {actionsItems(row).map((item, index) => (
                    item?.show && (
                      <li key={`action-item-${index}`}
                        className="uk-flex uk-flex-center">
                        {item.item}
                      </li>)
                  ))}
                </ul>
              </div>
            </>}
          </div>
        :
          <div className="uk-flex">
            {actionsItems(row).map((item) => (
              item?.show && <>{item.item}</>
            ))}
          </div>
      ),
    }
  ];

  const actionsItems = (row: IPersonCampaignDTO) => {
    return [{
      show: true,
      item: <Link
          to={`/campanhas/pessoas/${row.id}`}
          uk-tooltip={`title: ${isReadOnly ? 'Visualizar' : 'Ver/Editar'}`}
          className="uk-margin-small-right uk-text-primary"
        >
          <FontAwesomeIcon icon={isReadOnly ? faSearch : faPencil} size={isMobile ? "lg" : "1x"} />
        </Link>
      },
      {
        show: canChange,
        item: <a
            href="#"
            uk-tooltip="title: Pagamento"
            className="uk-margin-small-right uk-text-primary"
            onClick={(ev) => {
              ev.preventDefault();
              handlePayment(row);
            }}
          >
            <FontAwesomeIcon icon={faMoneyBill} size={isMobile ? "lg" : "1x"} />
          </a>
      },
      renderReminderOption(row),
      {
        show: canChange,
        item: <a
            href="#"
            uk-tooltip="title: Remover"
            className="uk-margin-small-right uk-text-primary"
            onClick={(ev) => {
              ev.preventDefault();
              handleRemove(row);
            }}
          >
            <FontAwesomeIcon icon={faTrash} size={isMobile ? "lg" : "1x"} />
          </a>
      }
    ] as MenuItem[];
  }

  useEffect(() => {
    setReadOnly(!authenticationService.canChangeCampaign());
    setCanChange(authenticationService.canChangeCampaign());
    init();
  }, []);

  const init = async () => {
    const { data: dataCongregations } = await api.get("congregations/dashboard");

    setCongregations(dataCongregations);

    setDebtYear(moment().year());
    setDebtMonth(moment().month() + 1);
  }

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setDelayedSearchTerm(termSearch);
    }, 500); // You can adjust the delay time according to your preferences

    return () => clearTimeout(delaySearch);
  }, [termSearch]);

  useEffect(() => {
    // Perform the search operation with delayedSearchTerm
    // This will be called only when the user stops typing for the specified delay time
    setRefresh(!refresh);
  }, [delayedSearchTerm]);

  useEffect(() => {
    setRefresh(!refresh);
  }, [debtYear, debtMonth]);

  const fetchPeople = async (page: number, limit: number) => {
    setTotalRows(0);
    try {
      let requestParams = {
        page: page,
        limit: limit,
        congregation: congSearch,
        dueAmount: dueAmount,
        dueDay: dueDay,
        isInDebt: isInDebt,
        welcomeNotDelivered: isWelcomeNotDelivered,
        year: isInDebt ? debtYear : undefined,
        month: isInDebt ? debtMonth : undefined
      } as any;

      setPageNo(page);

      if (termSearch) {
        requestParams = { ...requestParams, name: termSearch?.toUpperCase() };
      }

      const response = await api.get(`campaigns`, {
        params: requestParams
      });

      setTotalRows(response.data.total);

      return response;
    } catch {
      return { data: [], total: 0 };
    }
  };

  const handleRemove = async (row: IPersonDTO) => {
    setTypeDialog('delete');
    setDialogText([`Deseja realmente remover ${row.name}?`]);
    personSelected.current = row.id;
    setIsDialogOpen(true);
  };

  const dialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleSearchByName = async (search: string) => {
    if (search.length > 2 || search.length === 0) {
      setTermSearch(search);
    }
  };

  const handleCongregation = async(congregation: number) => {
    setCongSearch(congregation);
    setRefresh(!refresh);
  }

  const dialogConfirm = async (confirmationCode: string) => {
    try {
      if (typeDialog === 'reminder') {
        confirmReminderPayment();
        return;
      }

      if (!confirmationCode) {
        toast.warning('Informe a senha', toastOptions);
        return;
      }

      const dto = {} as IDeleteDTO;
      dto.confirmationCode = confirmationCode;
      dto.idDelete = +(personSelected.current);

      await api.post(`/campaigns/person/${personSelected.current}/delete`, dto);
      setIsDialogOpen(false);
      toast.success("Pessoa removida com sucesso!", toastOptions);
      setRefresh(!refresh);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    }
  };

  const handlePayment = async (row: IPersonCampaignDTO) => {
    setPerson(row);
    setAddPayment(true);
  };

  const afterHandleSubmit = (paidDate: Date) => {
    if (person) {
      person.paidDate = paidDate;
    }
    afterCloseModal();
    setUpdatedRow(!updatedRow);
  }

  const afterCloseModal = () => {
    setAddPayment(false);
    setPerson({} as IPersonCampaignDTO);    
  }

  const handleDueAmount = async(selectedDueAmount: number) => {
    setDueAmount(selectedDueAmount);
    setRefresh(!refresh);
  }

  const handleDueDay = async(selectedDueDay: number) => {
    setDueDay(selectedDueDay);
    setRefresh(!refresh);
  }

  const handleInDebt = async(selected: boolean) => {
    setIsInDebt(selected);
    setRefresh(!refresh);
  }

  const handleWelcomeNotDelivered = async(selected: boolean) => {
    setWelcomeNotDelivered(selected);
    setRefresh(!refresh);
  }

  const renderReminderOption = (row: IPersonCampaignDTO) => {
    if (!canChange) return;

    const today = moment();
    const lastBill = (row as any).lastBilling as IPersonBilling;
    const isInDebt = !lastBill.id || lastBill.year < today.year() || (lastBill.year === today.year() && lastBill.month < today.month() + 1);

    if (isInDebt) {
      return {
        show: canChange,
        item: <a
          href="#"
          uk-tooltip="title: Enviar Lembrete de Pagamento; pos: left"
          className="uk-margin-small-right uk-text-primary"
          onClick={(ev) => {
            ev.preventDefault();
            handleReminderPayment(row);
          }}
        >
          {reminderIndex === row.id ? <div uk-spinner="true; ratio:0.4"></div> : <FontAwesomeIcon icon={faBell} size={isMobile ? "lg" : "1x"} />}
        </a>
      }
    }
  }

  const handleReminderPayment = async (row: IPersonCampaignDTO) => {
    setTypeDialog('reminder');
    setIsDialogOpen(true);
    setDialogText([
      `Enviando lembrete de pagamento para ${row.name}.`,
      `${row.lastReminderAt ? `Último aviso enviado em ${displayDateTime(row.lastReminderAt)}` : 'Nenhum aviso enviado'}.`
    ]);
    setPerson(row);    
  };

  const confirmReminderPayment = async () => {
    setIsDialogOpen(false);
    setDialogText([]);
    try {
      if (!person) return;

      setReminderIndex(person.id);
      person.numOfPayment = (person.noOfPaymentsPaid ?? 0) + 1;
      const { data } = await api.post(`/campaigns/reminder/${person.id}`, person);

      if (data.item == true) {
        toast.success(`Lembrete de pagamento enviado para ${person.name} com sucesso!`, toastOptions);
      } else {
        toast.error(`Erro ao enviar lembrete de pagamento para ${person.name}.`, toastOptions);
      }
      setUpdatedRow(!updatedRow);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setPerson(undefined);
      setReminderIndex(-1);
    }
  };

  return (
    <div id="page-campaign-list">
      <div className="uk-card uk-card-default uk-card-small uk-card-hover uk-margin-bottom">
        <div className="uk-card-header">
          <div className="uk-grid-small uk-flex-middle" uk-grid="true">
            <div className="uk-width-expand">
              <h3 className="uk-card-title uk-margin-remove-bottom">
                Doadores Ativos (total de {totalRows})
              </h3>
            </div>
          </div>
        </div>
        <div className="uk-card-body">
          <div className="uk-flex@m uk-flex-middle">
            <div className="uk-inline-clip uk-transition-toggle uk-margin-small-right">
              <div className="uk-transition-scale-up uk-transition-opaque">
                <Link to="/campanhas/pessoas/novo" className="uk-button uk-button-small uk-button-primary">
                  {`Adicionar `} <FontAwesomeIcon icon={faPlus} size='xl' />
                </Link>
              </div>
            </div>
            {
              !authenticationService.isAuxCampaign() &&
                <div className="uk-inline-clip uk-transition-toggle uk-margin-small-right">
                  <div className="uk-transition-scale-up uk-transition-opaque">
                    <Link to="/campanhas" className="uk-button uk-button-small uk-button-default">
                      Voltar
                    </Link>
                  </div>
                </div>
            }
          </div>
          <div className="uk-grid-small uk-margin-top" uk-grid="true">
            <div className="uk-search uk-search-default uk-width-1-3@s">
              <span
                className="uk-search-icon-flip"
                uk-search-icon="true"
              ></span>
              <input
                className="uk-search-input uk-input uk-form-small"
                type="search"
                placeholder="Pesquise por nome..."
                style={{ textTransform: "uppercase" }}
                onChange={(ev) => {
                  handleSearchByName(ev.target.value);
                }}
              />
            </div>
            <div className="uk-flex uk-flex-middle uk-width-1-3@s">
              <label
                className="uk-form-label uk-margin-right"
                htmlFor="congregationId"
              >
                Congregação
              </label>
              <div
                uk-form-custom="target: > * > span:first-child"
                className="uk-flex-1"
              >
                <select
                  className="uk-form-small"
                  id="congregationId"
                  name="congregationId"
                  value={congSearch}
                  onChange={(e) => handleCongregation(+(e.target.value))}
                >
                  <option value="-1" key={`congregation-1`}>
                    Todas
                  </option>
                  {congregations.map((f) => {
                    return (
                      <option value={f.id} key={`congregation${f.id}`}>
                        {f.description}
                      </option>
                    );
                  })}
                </select>
                <button
                  className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                  style={{ textTransform: "none" }}
                  type="button"
                  tabIndex={-1}
                >
                  <span></span>
                  <span uk-icon="icon: chevron-down"></span>
                </button>
              </div>
            </div>
            {
              canChange && <>              
                <div className="uk-width-1-6@s">
                  <div className="uk-flex uk-flex-middle">                
                    <label className="uk-form-label uk-margin-small-right" htmlFor="dueAmount">Valor</label>
                    <div uk-form-custom="target: > * > span:first-child" 
                      className="uk-width-1"
                      style={{backgroundColor: '#FFF'}}>
                      <select className="uk-form-small"
                        id="dueAmount"
                        name="dueAmount"
                        value={dueAmount}
                        onChange={(e) => {handleDueAmount(+e.target.value);}}
                      >                                    
                        <option value="">Todos</option>
                        <option value={50} key={`dueAmount1`}>R$ 50,00</option>
                        <option value={100} key={`dueAmount2`}>R$ 100,00</option>
                        <option value={150} key={`dueAmount3`}>R$ 150,00</option>
                        <option value={200} key={`dueAmount4`}>R$ 200,00</option>
                      </select>
                      <button 
                        className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                        style={{textTransform: 'none'}} 
                        type="button" 
                        tabIndex={-1}
                      >
                        <span></span>
                        <span uk-icon="icon: chevron-down"></span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="uk-width-1-6@s">
                  <div className="uk-flex uk-flex-middle">
                    <label className="uk-form-label uk-margin-small-right" htmlFor="dueDay">Dia</label>
                    <div uk-form-custom="target: > * > span:first-child" 
                      className="uk-width-1"
                      style={{backgroundColor: '#FFF'}}>
                      <select className="uk-form-small"
                        id="dueDay"
                        name="dueDay"
                        value={dueDay}
                        onChange={(e) => {handleDueDay(+e.target.value);}}
                      >                                    
                        <option value="">Todos</option>
                        <option value={5} key={`dueDay1`}>Dia 5</option>
                        <option value={10} key={`dueDay2`}>Dia 10</option>
                        <option value={20} key={`dueDay3`}>Dia 20</option>
                        <option value={30} key={`dueDay4`}>Dia 30</option>
                      </select>
                      <button 
                        className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                        style={{textTransform: 'none'}} 
                        type="button" 
                        tabIndex={-1}
                      >
                        <span></span>
                        <span uk-icon="icon: chevron-down"></span>
                      </button>
                    </div>
                  </div>
                </div>                
                <div className="uk-width-1-5@s">
                  <label className="uk-form-label uk-margin-small-right" htmlFor="type">WhatsApp não entregue?</label>
                  <input className="uk-checkbox"
                    name="welcomeNotDelivered"
                    onChange={(e) => {
                      handleWelcomeNotDelivered(e.target.checked);
                    }}
                    type="checkbox"/>
                </div>
                <div className="uk-width-1-6@s">
                  <label className="uk-form-label uk-margin-small-right" htmlFor="type">Em débito?</label>
                  <input className="uk-checkbox"
                    name="isInDebt"
                    onChange={(e) => {
                      handleInDebt(e.target.checked);
                    }}
                    type="checkbox"/>
                </div>
                {
                  isInDebt && <>
                    <div className="uk-width-1-6@s">
                      <div className="uk-flex uk-flex-middle">
                        <label className="uk-form-label uk-margin-small-right" htmlFor="year">Ano</label>
                        <input type="number" 
                          id="year" 
                          name="year" 
                          placeholder="Ano" 
                          className="uk-input uk-form-small"
                          defaultValue={debtYear}
                          onChange={(e) => { setDebtYear(+e.target.value) }}
                        />
                      </div>
                    </div>
                    <div className="uk-width-1-6@s">
                      <div className="uk-flex uk-flex-middle uk-form-controls">                        
                        <label className="uk-form-label uk-margin-small-right" htmlFor="month">Mês</label>
                        <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                          <select className="uk-form-small"
                            id="month"
                            name="month"
                            defaultValue={debtMonth}
                            onChange={(e) => {
                              setDebtMonth(+e.target.value);
                            }}
                          >
                            {
                              months.map((m, i) => {
                                return <option key={`month${i+1}`} value={i+1}>{m.charAt(0).toUpperCase() + m.slice(1).toLowerCase()}</option>
                              })
                            }
                          </select>
                          <button
                            className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                            style={{textTransform: 'none'}} 
                            type="button" 
                            tabIndex={-1}
                          >
                            <span>{months[(debtMonth ?? 0) - 1]}</span>
                            <span uk-icon="icon: chevron-down"></span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                }               
              </>
            }
          </div>
          <TablePagination
            columns={columns}
            fetchData={fetchPeople}
            refresh={refresh}
            updateRow={updatedRow}
            pageNo={pageNo}
          />
        </div>
      </div>
      <DialogConfirm
        open={isDialogOpen}
        text={dialogText}
        afterClose={dialogClose}
        confirmTextLabel={typeDialog === 'reminder' ? '' : 'Informe sua senha'}
        handleConfirmation={(confirmationCode) => dialogConfirm(confirmationCode)}
      />
      <CampaignPayment 
        open={addPayment} 
        personCampaign={person}
        afterHandleSubmit={afterHandleSubmit} 
        afterCloseModal={afterCloseModal}        
      />
    </div>
  );
}